import boxen from "boxen";
// `avatar login [--reset]` — Command 01 spec.
// Implements the user-facing flow: announce verification URL, open browser,
// poll Google until token returned, validate domain, persist credentials.
import type { Command } from "commander";
import open from "open";
import { appendAuditEntry } from "../lib/audit-log-appender.js";
import { printAvatarBanner } from "../lib/avatar-ascii-banner.js";
import {
  buildUserConfig,
  buildVerificationUrl,
  decodeIdToken,
  pollForToken,
  requestDeviceCode,
  revokeToken,
  verifyHostedDomain,
} from "../lib/google-oauth-device-flow.js";
import { chalk, log, spinner } from "../lib/terminal-logger.js";
import {
  clearUserConfig,
  isTokenExpired,
  readUserConfig,
  USER_CONFIG_PATH,
  writeUserConfig,
} from "../lib/user-config-store.js";

export function registerLoginCommand(program: Command): void {
  program
    .command("login")
    .description("Đăng nhập Google SSO (workspace @nal.vn)")
    .option("--reset", "Xóa credential cũ và đăng nhập lại")
    .action(async (opts: { reset?: boolean }) => {
      try {
        await runLogin(opts);
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

// Exported để các command khác (vd init) reuse login flow khi user chưa login.
// Throw nếu login fail, hoặc return void khi success (user config đã write xong).
export async function runLogin(opts: { reset?: boolean }): Promise<void> {
  // Banner trước khi vào device-code flow để user nhận diện thương hiệu.
  printAvatarBanner({ tagline: "Đăng nhập Google SSO · workspace @nal.vn" });

  // Step 1 of spec: short-circuit if already logged in and token is still good.
  if (opts.reset) {
    await clearUserConfig();
    await appendAuditEntry("login_reset");
  } else {
    const existing = await readUserConfig();
    if (existing && !isTokenExpired(existing)) {
      log.success(`Đã đăng nhập: ${existing.email}`);
      return;
    }
  }

  // Step 2: request device + user code.
  const deviceSpinner = spinner("Đang yêu cầu device code từ Google...");
  let deviceCode: Awaited<ReturnType<typeof requestDeviceCode>>;
  try {
    deviceCode = await requestDeviceCode();
    deviceSpinner.succeed("Nhận device code");
  } catch (err) {
    deviceSpinner.fail("Không kết nối được Google");
    throw err;
  }

  // Step 3: display instructions to user.
  const verificationUrl = buildVerificationUrl(deviceCode);
  const instructions = [
    `1. Truy cập:    ${chalk.cyan(deviceCode.verification_url)}`,
    `2. Nhập code:   ${chalk.bold.yellow(deviceCode.user_code)}`,
    "",
    `Hoặc Avatar tự mở browser, click ${chalk.green("Allow")}...`,
  ].join("\n");
  process.stdout.write(`${boxen(instructions, { padding: 1, borderStyle: "round" })}\n`);

  // Step 4: open browser. Failure here is non-fatal — user can copy URL manually.
  void open(verificationUrl).catch(() => {
    log.dim("(Không mở được browser tự động — copy URL ở trên)");
  });

  // Step 5: poll token endpoint until success or expiry.
  const waitSpinner = spinner("Đang chờ xác nhận trong browser...");
  const intervalMs = deviceCode.interval * 1000;
  const deadline = Date.now() + deviceCode.expires_in * 1000;

  let token = null;
  while (Date.now() < deadline) {
    await sleep(intervalMs);
    try {
      token = await pollForToken(deviceCode.device_code);
      if (token) break;
    } catch (err) {
      waitSpinner.fail("Xác thực thất bại");
      throw err;
    }
  }
  if (!token) {
    waitSpinner.fail("Hết hạn chờ (5 phút). Chạy lại 'avatar login'.");
    process.exit(1);
  }
  waitSpinner.succeed("Đã nhận token từ Google");

  // Step 6: verify hosted domain. Revoke token if claim is wrong.
  const claims = decodeIdToken(token.id_token);
  try {
    verifyHostedDomain(claims);
  } catch (err) {
    await revokeToken(token.access_token);
    throw err;
  }

  // Step 7: persist credentials with chmod 600.
  const userConfig = buildUserConfig(token, claims);
  await writeUserConfig(userConfig);
  await appendAuditEntry("login", userConfig.email);

  log.success(`Xác thực thành công: ${userConfig.email}`);
  log.success(`Verify hosted domain: ${claims.hd} ✓`);
  log.success(`Lưu credential vào ${USER_CONFIG_PATH} (chmod 600)`);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
